import { useSuperAdminAllShops } from "@babackapp/private-api-client/client/use-super-admin-all-shops";
import { useSuperAdminShopBookmarks } from "@babackapp/private-api-client/client/use-super-admin-shop-bookmarks";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@babackapp/ui/components/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@babackapp/ui/components/popover";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@babackapp/ui/components/sidebar";
import { ChevronsUpDown, Globe, Star, Store } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const MAX_SHOPS_SHOWN = 50;

/**
 * Picks the shop the admin works on. No shop = the global sections only;
 * a shop opens its own pages under /admin/shops/:shopId.
 */
export function AdminShopSelector({ shopId }: { shopId: string | null }) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  // Loaded with the admin (not on open, as in the Returns switcher): the
  // trigger shows the selected shop's name.
  const { data: allShops } = useSuperAdminAllShops(true);
  const { data: bookmarkedShops } = useSuperAdminShopBookmarks(true);

  const selected =
    shopId === null
      ? null
      : (allShops?.find((shop) => shop.id === shopId) ??
        bookmarkedShops?.find((shop) => shop.id === shopId));

  const needle = search.trim().toLowerCase();
  const matches = (shop: {
    id: string;
    name: string;
    shopifyUrl?: string | null;
  }) =>
    [shop.name, shop.shopifyUrl ?? "", shop.id].some((value) =>
      value.toLowerCase().includes(needle),
    );
  const bookmarked = (bookmarkedShops ?? []).filter(matches);
  const bookmarkedIds = new Set(bookmarked.map(({ id }) => id));
  const others = (allShops ?? [])
    .filter((shop) => !bookmarkedIds.has(shop.id) && matches(shop))
    .slice(0, MAX_SHOPS_SHOWN);

  function select(nextShopId: string | null) {
    setIsOpen(false);
    setSearch("");
    navigate(nextShopId ? `/admin/shops/${nextShopId}` : "/admin/shops");
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <SidebarMenuButton
              size="lg"
              tooltip={selected?.name ?? "Vue globale"}
              className="data-[state=open]:bg-sidebar-accent"
            >
              {selected ? <Store /> : <Globe />}
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {selected?.name ?? (shopId ? "Boutique" : "Vue globale")}
                </span>
                <span className="truncate text-xs text-sidebar-foreground/75">
                  {selected ? selected.shopifyUrl : "Toutes les boutiques"}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" align="start" side="bottom">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Rechercher une boutique…"
                value={search}
                onValueChange={setSearch}
              />
              <CommandList>
                <CommandEmpty>Aucune boutique</CommandEmpty>
                {!needle && (
                  <CommandGroup>
                    <CommandItem value="__global" onSelect={() => select(null)}>
                      <Globe className="size-4" />
                      Vue globale
                    </CommandItem>
                  </CommandGroup>
                )}
                {bookmarked.length > 0 && (
                  <CommandGroup heading="Épinglées">
                    {bookmarked.map((shop) => (
                      <CommandItem
                        key={shop.id}
                        value={shop.id}
                        onSelect={() => select(shop.id)}
                      >
                        <Star className="size-4" />
                        <span className="truncate">{shop.name}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {others.length > 0 && (
                  <CommandGroup heading="Boutiques">
                    {others.map((shop) => (
                      <CommandItem
                        key={shop.id}
                        value={shop.id}
                        onSelect={() => select(shop.id)}
                      >
                        <Store className="size-4" />
                        <span className="truncate">{shop.name}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
